import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import { omit, map, concat, isEmpty } from 'lodash';
import 'react-select/dist/react-select.css';

import TextField from 'core/TextField';

import { createStructuredSelector } from 'reselect';
import { selectCurrentMasterSettings } from '@lib/selectors';
import { connect } from 'react-redux';
import styles from './styles';
import SelectWrapped from './SelectWrapped';

class SelectField extends React.Component {
  constructor(props) {
    super(props);

    this.waitFor = this.waitFor.bind(this);
    this.onMenuScrollToBottom = this.onMenuScrollToBottom.bind(this);

    this.state = {
      value: null,
      input: null,
      options: null,
      page: 1,
      loading: false,
    };
  }

  componentWillMount() {
    const { options } = this.props;

    if (!options) return;

    const newOptions = this.optionsInterceptor(options);

    this.setState({
      options: newOptions,
      loading: false,
    });
  }

  componentWillUpdate(nextProps) {
    const { isLoading } = this.props;

    if (isLoading !== nextProps.isLoading) this.setState({ loading: nextProps.isLoading || false });

    if (!nextProps.options || (nextProps.options === this.props.options && this.state.options)) return;

    const { page } = this.state;
    const options = this.optionsInterceptor(nextProps.options);

    this.setState({ options: page === 1 ? options : concat(this.state.options || [], options) });
  }

  optionsInterceptor(options) {
    if (!options) return [];
    const { currentMasterSettings } = this.props;
    const shouldUseAlternativeName = currentMasterSettings?.appointment?.useAlternativeNameInWaitlistUI;

    return map(options, (option) => {
      const label = option.label || option.name || option.labelOnly;
      const alternativeName = option.alternativeName || label;

      return {
        value: option.value || option._id || option.id || option.label || option.name || null,
        label: shouldUseAlternativeName ? alternativeName : label,
        description: option.description,
        clearableValue: option.clearableValue !== undefined ? option.clearableValue : true,
      };
    });
  }

  handleChange = (value) => {
    this.setState({ value });
  };

  waitFor(callback, loading) {
    if (loading || this.state.loading) {
      return setTimeout(this.waitFor.bind(null, callback), 500); /* this checks the flag every 100 milliseconds */
    }
    const { options } = this.state;
    return callback(null, { options });
  }

  handleFocus = (event) => {
    const { onFocus } = this.props;

    if (onFocus) onFocus(event);
  };

  handleLoadOptions = (input, callback) => {
    // FIXME(danielmana): skip loading page when an item is selected after async search
    if (input === this.state.input) {
      const { options } = this.state;
      callback(null, { options });
      return;
    }
    const { loadOptions } = this.props;
    const page = 1;
    this.setState({ page, input, loading: true });
    loadOptions(input, page);
    this.waitFor(callback, true);
  };

  onMenuScrollToBottom() {
    // TODO(danielmana): skip call if no more pages
    const { loadOptions, onFinishScroll } = this.props;

    if (typeof onFinishScroll === 'function') {
      onFinishScroll();
    }

    if (!loadOptions) {
      return;
    }

    const page = this.state.page + 1;
    this.setState({ page, loading: true });
    loadOptions(this.state.input, page);

    return new Promise(this.waitFor);
  }

  render() {
    const {
      theme,
      classes,
      classNameWrapper,
      clearable,
      clearIconClassName,
      clearIconRenderer,
      disableUnderline,
      filterOption,
      filterOptions,
      hideArrow,
      hideNoResults,
      inputClassName,
      loadOptions,
      multiple,
      onInputChange,
      onSelectResetsInput,
      optionComponent,
      placeholder,
      searchable,
      selectRef,
      startAdornment,
      valueComponent,
      InputLabelProps,
      rounded,
      dataLhId,
      InputProps,
      truncate,
      onFinishScroll,
      ...other
    } = this.props;
    const { loading, options } = this.state;

    const hasNullOption = options && options.length > 0 && options.filter((option) => option.value === null).length > 0;
    const rootClass = other.size === 'small' ? classes.smallRoot : classes.root;

    return (
      <div className={classnames(rootClass, classNameWrapper, { [classes.startAdornment]: Boolean(startAdornment) })}>
        <TextField
          value={this.state.value}
          onChange={this.handleChange}
          InputLabelProps={!isEmpty(InputLabelProps) ? InputLabelProps : { className: classes.label }}
          InputProps={{
            inputComponent: SelectWrapped,
            onFocus: this.handleFocus,
            classes: {
              input: classnames(
                classes.textFieldInput,
                {
                  [classes.textFieldUnderline]: disableUnderline === false, // compatibility, default disabled
                  [classes.rounded]: rounded,
                },
                inputClassName,
              ),
              root: classnames({
                [classes.rounded]: rounded,
                [classes.nullOption]: this.state.value === null && hasNullOption,
              }),
              disabled: classes.disabled,
            },
            inputProps: {
              clearable,
              clearIconClassName,
              rounded,
              clearIconRenderer,
              filterOption,
              filterOptions,
              hideArrow,
              hideNoResults,
              isLoading: loading,
              multi: multiple,
              searchable,
              simpleValue: true,
              loadOptions: loadOptions ? this.handleLoadOptions : null,
              optionComponent,
              options: options || [],
              onMenuScrollToBottom: this.onMenuScrollToBottom,
              onInputChange,
              onSelectResetsInput,
              placeholder: placeholder || 'Select',
              selectRef,
              valueComponent,
              truncate,
            },
            startAdornment,
            disableUnderline: disableUnderline !== false, // compatibility, default disabled
            ...InputProps,
          }}
          {...omit(other, ['isLoading', 'options', 'onFocus'])}
        />
      </div>
    );
  }
}

SelectField.propTypes = {
  classNameWrapper: PropTypes.string,
  classes: PropTypes.object.isRequired,
  clearIconClassName: PropTypes.string,
  clearIconRenderer: PropTypes.node,
  clearable: PropTypes.bool,
  currentMasterSettings: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  disableUnderline: PropTypes.bool,
  filterOption: PropTypes.func,
  filterOptions: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  hideArrow: PropTypes.bool,
  hideNoResults: PropTypes.bool,
  inputClassName: PropTypes.string,
  isLoading: PropTypes.bool,
  loadOptions: PropTypes.func,
  multiple: PropTypes.bool,
  onFocus: PropTypes.func,
  onInputChange: PropTypes.func,
  onSelectResetsInput: PropTypes.bool,
  optionComponent: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  placeholder: PropTypes.string,
  searchable: PropTypes.bool,
  selectRef: PropTypes.func,
  startAdornment: PropTypes.node,
  theme: PropTypes.object.isRequired,
  value: PropTypes.string,
  rounded: PropTypes.bool,
  valueComponent: PropTypes.func,
  InputLabelProps: PropTypes.object,
  InputProps: PropTypes.object,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string,
  error: PropTypes.bool,
  floatingLabel: PropTypes.bool,
  truncate: PropTypes.bool,
  helperText: PropTypes.string,
  showRequiredMark: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  size: PropTypes.string,
  onFinishScroll: PropTypes.func,
};

SelectField.defaultProps = {
  hideArrow: false,
  multiple: false,
  searchable: true,
  disabled: false,
  InputLabelProps: {},
  InputProps: {},
};

const mapStateToProps = createStructuredSelector({
  currentMasterSettings: selectCurrentMasterSettings,
});

export default connect(mapStateToProps)(withStyles(styles, { withTheme: true })(SelectField));
